import { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, ShieldOff, Loader2, Trash2, Smartphone, Monitor, Clock, X } from 'lucide-react';
import { 
  getMfaFactors, 
  unenrollFactor, 
  disableMfaForUser, 
  updateCrossPanelMfa,
  getTrustedDevices, 
  removeTrustedDevice,
  checkCrossPanelMfa
} from '../lib/mfaService';
import { MFAEnroll } from './MFAEnroll';

interface MFASettingsProps {
  userId: string;
  onClose: () => void;
  onStatusChange?: () => void;
}

interface TrustedDevice {
  id: string;
  device_name: string | null;
  browser_info: string | null;
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string | null;
}

export function MFASettings({ userId, onClose, onStatusChange }: MFASettingsProps) {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isCrossPanelEnabled, setIsCrossPanelEnabled] = useState(false);
  const [showEnroll, setShowEnroll] = useState(false);
  const [trustedDevices, setTrustedDevices] = useState<TrustedDevice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDisabling, setIsDisabling] = useState(false);
  const [showConfirmDisable, setShowConfirmDisable] = useState(false);
  const [factorId, setFactorId] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const [factors, crossPanel, devices] = await Promise.all([
        getMfaFactors(),
        checkCrossPanelMfa(userId),
        getTrustedDevices(userId),
      ]);

      const verifiedFactor = factors.totp.find(f => f.status === 'verified');
      setIsEnabled(!!verifiedFactor);
      setFactorId(verifiedFactor?.id || null);
      setIsCrossPanelEnabled(crossPanel);
      setTrustedDevices(devices);
    } catch (err) {
      console.error('Error loading MFA status:', err);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleDisable = async () => {
    if (!factorId) return;
    setIsDisabling(true);
    try {
      await unenrollFactor(factorId);
      await disableMfaForUser(userId);
      await updateCrossPanelMfa(userId, false);
      setIsEnabled(false);
      setFactorId(null);
      setShowConfirmDisable(false);
      setTrustedDevices([]);
      onStatusChange?.();
    } catch (err: unknown) {
      console.error('Error disabling 2FA:', err);
    } finally {
      setIsDisabling(false);
    }
  };

  const handleRemoveDevice = async (deviceId: string) => {
    try {
      await removeTrustedDevice(deviceId);
      setTrustedDevices(prev => prev.filter(d => d.id !== deviceId));
    } catch (err) {
      console.error('Error removing device:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-lg w-full mx-4">
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-green-600" />
            <p className="mt-4 text-sm text-gray-600">Loading 2FA settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {isEnabled ? (
              <ShieldCheck className="w-6 h-6 text-green-600" />
            ) : (
              <ShieldOff className="w-6 h-6 text-gray-400" />
            )}
            <h2 className="text-lg font-semibold text-gray-900">Two-Factor Authentication</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Enrollment Flow */}
          {showEnroll ? (
            <MFAEnroll
              userId={userId}
              onEnrolled={() => {
                setShowEnroll(false);
                loadStatus();
                onStatusChange?.();
              }}
              onCancelled={() => setShowEnroll(false)}
            />
          ) : (
            <>
              {/* Status */}
              <div className={`rounded-lg p-4 ${isEnabled ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {isEnabled ? (
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <ShieldCheck className="w-5 h-5 text-green-600" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                        <ShieldOff className="w-5 h-5 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-gray-900">
                        {isEnabled ? '2FA is Active' : '2FA is Not Enabled'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {isEnabled
                          ? 'Your account is protected with an authenticator app.'
                          : 'Add an extra layer of security to your account.'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Cross-panel indicator */}
                {isCrossPanelEnabled && !isEnabled && (
                  <div className="mt-3 p-2 bg-blue-50 rounded-md">
                    <p className="text-xs text-blue-700">
                      ℹ️ 2FA is enabled in another AI Agent HUB panel. Enable it here too for consistent security.
                    </p>
                  </div>
                )}
              </div>

              {/* Enable / Disable Button */}
              {isEnabled ? (
                <>
                  {showConfirmDisable ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
                      <p className="text-sm text-red-800 font-medium">
                        Are you sure you want to disable 2FA?
                      </p>
                      <p className="text-xs text-red-600">
                        This will remove the authenticator app protection and delete all trusted devices.
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowConfirmDisable(false)}
                          className="flex-1 py-2 px-3 bg-white text-gray-700 rounded-md border border-gray-200 hover:bg-gray-50 text-sm font-medium transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleDisable}
                          disabled={isDisabling}
                          className="flex-1 py-2 px-3 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium disabled:opacity-50 transition-colors flex items-center justify-center"
                        >
                          {isDisabling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Yes, Disable 2FA'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowConfirmDisable(true)}
                      className="w-full py-2 px-4 bg-gray-100 text-red-600 rounded-md hover:bg-red-50 border border-gray-200 text-sm font-medium transition-colors"
                    >
                      Disable 2FA
                    </button>
                  )}
                </>
              ) : (
                <button
                  onClick={() => setShowEnroll(true)}
                  className="w-full py-3 px-4 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <ShieldCheck className="w-4 h-4" />
                  Enable Two-Factor Authentication
                </button>
              )}

              {/* Trusted Devices */}
              {isEnabled && trustedDevices.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-gray-500" />
                    Trusted Devices
                  </h3>
                  <div className="space-y-2">
                    {trustedDevices.map((device) => {
                      const isExpired = device.expires_at && new Date(device.expires_at) < new Date();
                      return (
                        <div
                          key={device.id}
                          className={`flex items-center justify-between p-3 rounded-md border ${
                            isExpired ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-white border-gray-200'
                          }`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <Monitor className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {device.device_name || 'Unknown Device'}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <Clock className="w-3 h-3" />
                                {isExpired ? (
                                  <span className="text-red-500">Expired</span>
                                ) : device.expires_at ? (
                                  <span>Expires {new Date(device.expires_at).toLocaleDateString()}</span>
                                ) : null}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveDevice(device.id)}
                            className="p-1.5 hover:bg-red-50 rounded-md transition-colors flex-shrink-0"
                            title="Remove trusted device"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
